import React from 'react';
import { Pencil, Trash2, MessageSquare } from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';
import NoImageTile from '@/components/NoImageTile';

/**
 * CartItemCard - Individual cart line item display
 * 
 * Features:
 * - Item thumbnail, name, quantity, line total
 * - Selected modifiers displayed cleanly
 * - Special instructions (if any)
 * - Edit and Remove buttons
 * - Premium dark theme
 */
export default function CartItemCard({
  item,
  onEdit,
  onRemove,
  disabled = false,
  className = '',
}) {
  const {
    id,
    name,
    image_url,
    modifiers = [],
    quantity,
    totalPrice,
    basePrice,
    instructions,
  } = item;

  const lineTotal = (totalPrice || basePrice) * quantity;
  const hasModifiers = modifiers.length > 0;
  const hasInstructions = instructions && instructions.trim();

  const [imageFailed, setImageFailed] = React.useState(false);

  return (
    <div 
      className={`
        bg-[#1a1f26] border border-white/10 rounded-2xl
        overflow-hidden
        ${disabled ? 'opacity-60' : ''}
        ${className}
      `}
    >
      <div className="p-4 sm:p-5">
        <div className="flex gap-4">
          {/* Thumbnail */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-xl overflow-hidden bg-[#252b33]">
            {image_url && !imageFailed ? (
              <img
                src={image_url}
                alt={name}
                className="w-full h-full object-cover"
                onError={() => setImageFailed(true)}
              />
            ) : (
              <NoImageTile name={name} dark className="w-full h-full" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header Row */}
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="text-lg font-semibold text-white leading-tight">
                {name}
              </h3>
              <span className="text-lg font-bold text-[#f5a623] whitespace-nowrap">
                {formatCurrency(lineTotal)}
              </span>
            </div>

            {/* Quantity */}
            <p className="text-sm text-gray-400 mb-2">
              Qty: {quantity} × {formatCurrency(totalPrice || basePrice)}
            </p>

            {/* Modifiers */}
            {hasModifiers && (
              <div className="mb-2">
                <p className="text-sm text-gray-500">
                  {modifiers.map(m => m.name).join(', ')}
                </p>
              </div>
            )}

            {/* Special Instructions */}
            {hasInstructions && (
              <div className="flex items-start gap-2 mt-2 p-2 rounded-lg bg-[#252b33]">
                <MessageSquare size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-400 italic line-clamp-2">
                  "{instructions}"
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={() => onEdit?.(item)}
            disabled={disabled}
            className="
              flex-1 h-12 
              flex items-center justify-center gap-2
              bg-[#252b33] border border-white/10
              text-white text-sm font-medium
              rounded-xl
              transition-all
              hover:bg-[#2d343d] hover:border-white/20
              active:scale-[0.98]
              disabled:opacity-50 disabled:cursor-not-allowed
            "
            aria-label={`Edit ${name}`}
          >
            <Pencil size={16} />
            <span>Edit</span>
          </button>
          <button
            onClick={() => onRemove?.(item)}
            disabled={disabled}
            className="
              h-12 w-12
              flex items-center justify-center
              bg-[#252b33] border border-white/10
              text-gray-400
              rounded-xl
              transition-all
              hover:bg-[#ef4444]/10 hover:border-[#ef4444]/30 hover:text-[#ef4444]
              active:scale-[0.98]
              disabled:opacity-50 disabled:cursor-not-allowed
            "
            aria-label={`Remove ${name}`}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
